import { forwardRef, useCallback } from "react";
import { colorForName } from "@/domain/colors";
import styles from "./ShiftChip.module.css";


interface ShiftChipProps {
  readonly shiftId: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly cleanerName: string;
  readonly membershipId: string;
  readonly status: "draft" | "published";
  readonly hasClash?: boolean;
  readonly highlighted?: boolean;
  readonly dimmed?: boolean;
  readonly active?: boolean;
  readonly onClick: () => void;
  readonly onDragStart?: (shiftId: string) => void;
  readonly onDragEnd?: () => void;
}

export const ShiftChip = forwardRef<HTMLButtonElement, ShiftChipProps>(function ShiftChip(
  { shiftId, startTime, endTime, cleanerName, membershipId, status, hasClash, highlighted, dimmed, active, onClick, onDragStart: onDragStartProp, onDragEnd: onDragEndProp },
  ref,
) {
  const parts = cleanerName.trim().split(/\s+/);
  const firstName = parts.length > 1
    ? `${parts[0]} ${parts[parts.length - 1][0]}`
    : parts[0];
  const color = colorForName(cleanerName);

  const classes = [
    styles.chip,
    styles[status],
    highlighted ? styles.highlighted : "",
    dimmed ? styles.dimmed : "",
    active ? styles.active : "",
  ].filter(Boolean).join(" ");

  const chipStyle = status === "published"
    ? { "--chip-color": color } as React.CSSProperties
    : undefined;

  const handleDragStart = useCallback((e: React.DragEvent) => {
    e.dataTransfer.setData("text/plain", shiftId);
    e.dataTransfer.effectAllowed = "move";

    const el = e.currentTarget as HTMLElement;
    const clone = el.cloneNode(true) as HTMLElement;
    clone.style.transform = "rotate(3deg)";
    clone.style.boxShadow = "0 8px 24px rgba(0,0,0,0.15)";
    clone.style.width = `${el.offsetWidth}px`;
    clone.style.position = "absolute";
    clone.style.top = "-9999px";
    document.body.appendChild(clone);
    e.dataTransfer.setDragImage(clone, el.offsetWidth / 2, el.offsetHeight / 2);
    requestAnimationFrame(() => document.body.removeChild(clone));

    el.classList.add(styles.dragging);
    onDragStartProp?.(shiftId);
  }, [shiftId, onDragStartProp]);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).classList.remove(styles.dragging);
    onDragEndProp?.();
  }, [onDragEndProp]);

  return (
    <button
      ref={ref}
      type="button"
      className={classes}
      style={chipStyle}
      onClick={onClick}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      data-highlighted={highlighted ? "true" : undefined}
      data-membership-id={membershipId}
    >
      {hasClash && <span className={styles.clashDot} aria-label="Scheduling clash" />}
      <span className={styles.info}>
        <span className={styles.name}>{firstName}</span>
        <span className={styles.time}>{startTime}-{endTime}</span>
      </span>
    </button>
  );
});
